import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getAppointmentIdFromConversation } from '../../helpers/chats';
import { UserConversation } from '../../types/chats';
import useGetAppointment from '../appointments/useGetAppointment';
import useGetConversationAppointment from './useGetConversationAppointment';

function useGetChatHeaderAppointment(conversation: UserConversation | null) {
  const { conversationId: routeConversationId } = useParams<{
    conversationId: string;
  }>();
  const [searchParams] = useSearchParams();

  const appointmentIdFromParam =
    searchParams.get('appointment') || searchParams.get('request');

  const appointmentIdFromConversation = conversation
    ? getAppointmentIdFromConversation(conversation)
    : null;
  const appointmentId = appointmentIdFromConversation;
  const resolvedAppointmentId =
    appointmentIdFromConversation || appointmentIdFromParam || null;

  const {
    data,
    isPlaceholderData,
    isLoading: isLoadingAppointment,
    isEnabled: isEnabledAppointment,
  } = useGetAppointment(appointmentId);
  const {
    data: conversationAppointmentData,
    isPlaceholderData: isConversationAppointmentPlaceholderData,
    isLoading: isLoadingConversationAppointment,
    isEnabled: isEnabledConversationAppointment,
  } = useGetConversationAppointment(conversation?.id, appointmentIdFromParam, {
    enabled:
      !!conversation &&
      !isEnabledAppointment &&
      conversation.id === routeConversationId,
  });

  const isEnabled = isEnabledAppointment || isEnabledConversationAppointment;
  const isLoading =
    (isEnabledAppointment && (isLoadingAppointment || isPlaceholderData)) ||
    (isEnabledConversationAppointment &&
      (isLoadingConversationAppointment ||
        isConversationAppointmentPlaceholderData));

  const appointment = React.useMemo(() => {
    if (!resolvedAppointmentId) return null;

    // Keep appointmentId available during placeholder/refetch so callers
    // (e.g. save-chat-photo) do not fall back to the customer path.
    if (appointmentIdFromConversation) {
      return {
        appointment: isPlaceholderData ? null : data ?? null,
        appointmentId: appointmentIdFromConversation,
      };
    }

    return {
      appointment: isConversationAppointmentPlaceholderData
        ? null
        : conversationAppointmentData ?? null,
      appointmentId: appointmentIdFromParam,
    };
  }, [
    appointmentIdFromConversation,
    appointmentIdFromParam,
    conversationAppointmentData,
    data,
    isConversationAppointmentPlaceholderData,
    isPlaceholderData,
    resolvedAppointmentId,
  ]);

  return {
    appointment: appointment?.appointment ?? null,
    appointmentId: resolvedAppointmentId,
    isLoading,
    isEnabled,
  };
}

export default useGetChatHeaderAppointment;
